import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css'; // Mandatory CSS required by the grid
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { formatNumberToCurrency } from '../../../helpers/utils';

const valueColumnBaseColumnOptions = {
  type: 'numericColumn',
  aggFunc: 'sum',
  valueFormatter: valueFormatter,
  cellStyle: (params) => getCellStyle(params.value)
};

const DATA_COLUMNS = [
  { 'column': 'contractedList', metricName: 'Contracted' },
  { 'column': 'actualsList', metricName: 'Actuals' },
];

const DATA_COLUMNS_PER_CHART_TYPE = {
  "MLA": [
    { 'column': 'mlasList', metricName: 'MLA' },
  ]
};

const CashFlowTableSummary = ({ data, period, yearFilter, chartType }) => {
  const gridData = useMemo(() => processDataForTableDisplay(data, period, yearFilter, chartType), [data, period, yearFilter, chartType]);
  const periodColumns = useMemo(() => getPeriodColumns(period, yearFilter), [period, yearFilter]);
  const singleAsset = useMemo(() => isSingleAsset(gridData), [gridData]);
  const colDefs = useMemo(
    () => [
      {
        field: 'metric',
        rowGroup: !singleAsset,
        hide: !singleAsset
      },
      {
        field: 'asset', headerName: 'Asset',
        hide: singleAsset
      },
      {
        ...valueColumnBaseColumnOptions,
        field: 'total',
        headerName: 'Total',
        cellStyle: (params) => ({ ...getCellStyle(params.value), backgroundColor: '#e6e6e6' })
      },
      ...periodColumns
    ],
    [periodColumns, singleAsset]
  );

  return (
    <div
      className="ag-theme-quartz" // applying the grid theme
      style={{
        height: 500,
        width: '100%',
        padding: '20px'
      }} // the grid will fill the size of the parent container
    >
      <AgGridReact
        columnDefs={colDefs}
        rowData={gridData}
        enableRangeSelection={true}
        autoSizeStrategy={{ type: 'fitCellContents' }}
        autoGroupColumnDef={{
          headerName: ''
        }}
        suppressAggFuncInHeader={true}
      />
    </div>
  );
};

export default CashFlowTableSummary;

CashFlowTableSummary.propTypes = {
  data: PropTypes.object,
  period: PropTypes.string
};

CashFlowTableSummary.defaultProps = {
  data: [],
  period: 'month'
};

const monthNumberToName = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec'
};

function processDataForTableDisplay(data, periodType, yearFilter, chartType) {
  if (!data) {
    return [];
  }

  const columns = DATA_COLUMNS_PER_CHART_TYPE[chartType] ?? DATA_COLUMNS;

  return columns.reduce((acc, item) => {
    acc.push(...createDimensionTable(data[item.column], item.metricName, periodType, yearFilter));
    return acc;
  }, []);
}

function createDimensionTable(data, metricName, periodType, yearFilter) {
  if (!data) {
    console.warn('No data is present for metric', metricName);
    return [];
  }

  const dimensionTable = data.reduce((acc, cur) => {
    if (!acc[cur.asset]) {
      acc[cur.asset] = getDefaultRowData(metricName, cur.asset, periodType, yearFilter);
    }

    acc[cur.asset][cur.period] = cur.value;
    acc[cur.asset].total += cur.value;

    return acc;
  }, {});

  return Object.values(dimensionTable);
}

function valueFormatter(params) {
  if (params.value == null) {
    return '';
  }
  return formatNumberToCurrency(params.value, 'EUR', '-', 0);
}

function getCellStyle(value) {
  if (value < 0) {
    return { color: 'red' };
  }
  if (value > 0) {
    return { color: 'green' };
  }

  return {};
}

function getDefaultRowData(metric, asset, periodType, yearFilter) {
  let periodFields = {};
  if (periodType === 'month') {
    for (let i = 1; i <= 12; i++) {
      periodFields[i] = 0;
    }
  } else {
    for (let i = yearFilter; i < yearFilter + 5; i++) {
      periodFields[i] = 0;
    }
  }

  return {
    metric,
    asset,
    total: 0,
    ...periodFields
  };
}

function getPeriodColumns(period, yearFilter) {
  const result = [];

  if (period === 'month') {
    for (let i = 1; i <= 12; i++) {
      result.push({
        ...valueColumnBaseColumnOptions,
        field: `${i}`,
        headerName: monthNumberToName[i]
      });
    }
  } else {
    for (let i = yearFilter; i < yearFilter + 5; i++) {
      result.push({ ...valueColumnBaseColumnOptions, field: `${i}` });
    }
  }

  return result;
}

function isSingleAsset(gridData) {
  const assets = gridData.reduce((acc, cur) => {
    if (!acc.includes(cur.asset)) {
      acc.push(cur.asset);
    }

    return acc;
  }, []);

  return assets.length === 1;
}